import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useOrganization } from "@/hooks/useOrganization";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { 
  Home, 
  LogOut, 
  User, 
  FileText, 
  BarChart3, 
  Settings, 
  TrendingUp,
  TrendingDown,
  Wallet,
  Target,
  Lightbulb,
  ChevronDown,
  ListTree,
  Landmark,
  Menu,
  GitCompareArrows,
} from "lucide-react";
import { UserProfileDialog } from "@/components/UserProfileDialog";
import { useIsMobile } from "@/hooks/use-mobile";

export const Header = () => {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { organization, currentMember } = useOrganization();
  const isMobile = useIsMobile();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isReportPage = [
    '/relatorio-vendas',
    '/analise-despesas',
    '/fluxo-caixa',
    '/relatorio-rentabilidade',
    '/recomendacoes',
    '/extrato-hierarquico',
    '/saldos-bancarios',
    '/orcamento'
  ].includes(location.pathname);

  const activeMainTab = (() => {
    if (isReportPage) return 'relatorios';
    if (location.pathname.startsWith('/lancamentos')) return 'lancamentos';
    if (location.pathname.startsWith('/conciliacao')) return 'conciliacao';
    if (location.pathname.startsWith('/cadastros')) return 'cadastros';
    return 'inicio';
  })();

  const isTabActive = (tab: string) => {
    return tab === activeMainTab;
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const navButtonBase = "font-medium gap-2 h-9 px-4 rounded-full text-sm transition-none";
  const navButtonActive = `${navButtonBase} bg-white !text-primary hover:bg-white hover:!text-primary focus:bg-white focus:!text-primary shadow-md`;
  const navButtonInactive = `${navButtonBase} text-white bg-transparent hover:bg-transparent hover:text-white focus:bg-transparent focus:text-white`;

  const rightButtonBase = "font-medium gap-2 h-8 px-3 rounded-full text-xs transition-none";
  const rightButtonStyle = `${rightButtonBase} text-white bg-transparent hover:bg-transparent hover:text-white focus:bg-transparent focus:text-white`;

  const reportLinks = [
    { to: "/relatorio-vendas", icon: TrendingUp, label: "Receitas", color: "text-emerald-500" },
    { to: "/analise-despesas", icon: TrendingDown, label: "Despesas", color: "text-red-500" },
    { to: "/saldos-bancarios", icon: Landmark, label: "Saldos Bancários", color: "text-teal-500" },
    { to: "/extrato-hierarquico", icon: ListTree, label: "DRE", color: "text-indigo-500" },
    { to: "/fluxo-caixa", icon: Wallet, label: "DFC", color: "text-purple-500" },
    { to: "/relatorio-rentabilidade", icon: Target, label: "Rentabilidade", color: "text-orange-500" },
    { to: "/orcamento", icon: Wallet, label: "Orçamento", color: "text-blue-500" },
    { to: "/recomendacoes", icon: Lightbulb, label: "Recomendações", color: "text-yellow-500" },
  ];

  // Mobile navigation in a Sheet drawer
  const MobileNav = () => (
    <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="text-white hover:bg-white/20 h-9 w-9">
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-72 p-0">
        <div className="p-4 border-b">
          <h2 className="text-lg font-bold">Radar Financeiro</h2>
          <p className="text-xs text-muted-foreground">Seu controle financeiro completo</p>
        </div>
        <nav className="flex flex-col p-2">
          <Link to="/" onClick={() => setMobileMenuOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium ${activeMainTab === 'inicio' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
            <Home className="h-4 w-4" /> Início
          </Link>
          <Link to="/lancamentos" onClick={() => setMobileMenuOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium ${activeMainTab === 'lancamentos' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
            <FileText className="h-4 w-4" /> Lançamentos
          </Link>
          
          <Link to="/conciliacao" onClick={() => setMobileMenuOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium ${activeMainTab === 'conciliacao' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
            <GitCompareArrows className="h-4 w-4" /> Conciliação
          </Link>

          {/* Reports sub-menu */}
          <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-2">Relatórios</div>
          {reportLinks.map(r => (
            <Link key={r.to} to={r.to} onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-6 py-2.5 rounded-lg text-sm ${location.pathname === r.to ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
              <r.icon className={`h-4 w-4 ${location.pathname === r.to ? '' : r.color}`} /> {r.label}
            </Link>
          ))}

          <Link to="/cadastros" onClick={() => setMobileMenuOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium mt-2 ${activeMainTab === 'cadastros' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
            <Settings className="h-4 w-4" /> Cadastros
          </Link>
        </nav>
      </SheetContent>
    </Sheet>
  );

  // Desktop/Tablet centered nav
  const DesktopNav = () => (
    <nav className="hidden md:flex items-center gap-1 z-10 rounded-full p-1">
      <Link to="/">
        <Button variant="ghost" size="sm" className={isTabActive('inicio') ? navButtonActive : navButtonInactive}>
          <Home className="h-4 w-4" />
          <span className="hidden lg:inline">Início</span>
        </Button>
      </Link>

      <Link to="/lancamentos">
        <Button variant="ghost" size="sm" className={isTabActive('lancamentos') ? navButtonActive : navButtonInactive}>
          <FileText className="h-4 w-4" />
          <span className="hidden lg:inline">Lançamentos</span>
        </Button>
      </Link>

      <Link to="/conciliacao">
        <Button variant="ghost" size="sm" className={isTabActive('conciliacao') ? navButtonActive : navButtonInactive}>
          <GitCompareArrows className="h-4 w-4" />
          <span className="hidden lg:inline">Conciliação</span>
        </Button>
      </Link>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={`${isTabActive('relatorios') ? navButtonActive : navButtonInactive} data-[state=open]:!bg-white data-[state=open]:!text-primary data-[state=open]:shadow-md cursor-pointer`}
          >
            <BarChart3 className="h-4 w-4" />
            <span className="hidden lg:inline">Relatórios</span>
            <ChevronDown className="h-3 w-3 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 bg-background/95 backdrop-blur-md border border-border/50 shadow-xl rounded-xl data-[state=open]:!animate-none data-[state=closed]:!animate-none">
          {reportLinks.map(r => {
            const isActive = location.pathname === r.to;
            return (
              <DropdownMenuItem
                key={r.to}
                asChild
                className={isActive ? "bg-primary/10 text-primary focus:bg-primary/15 focus:text-primary data-[highlighted]:bg-primary/15 data-[highlighted]:text-primary" : ""}
              >
                <Link to={r.to} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer">
                  <r.icon className={`h-4 w-4 ${isActive ? 'text-primary' : r.color}`} />
                  <span className={isActive ? 'font-semibold' : ''}>{r.label}</span>
                </Link>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <Link to="/cadastros">
        <Button variant="ghost" size="sm" className={isTabActive('cadastros') ? navButtonActive : navButtonInactive}>
          <Settings className="h-4 w-4" />
          <span className="hidden lg:inline">Cadastros</span>
        </Button>
      </Link>
    </nav>
  );

  return (
    <header className="bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground shadow-lg relative">
      <div className="w-full page-bleed py-3 md:py-4 flex items-center justify-between md:justify-center gap-2 max-w-[1600px] mx-auto">
        {/* Left: Logo + hamburger on mobile */}
        <div className="flex items-center gap-2 md:flex-shrink min-w-0 md:mr-auto">
          {isMobile && <MobileNav />}
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-white tracking-tight whitespace-nowrap">Radar Financeiro</h1>
            <p className="text-white/60 text-xs mt-0.5 hidden sm:block">Seu controle financeiro completo</p>
          </div>
        </div>

        {/* Center: Desktop/Tablet nav */}
        <DesktopNav />
        
        {/* Right: User actions */}
        {user && (
          <div className="md:ml-auto flex items-center gap-1 flex-shrink-0">
            <Button 
              variant="ghost" 
              size="sm"
              className={`${rightButtonStyle} cursor-default pointer-events-none hidden lg:flex`}
            >
              <span className="font-semibold whitespace-nowrap overflow-hidden text-ellipsis max-w-[180px]">
                {organization?.name || '\u00A0'}
              </span>
            </Button>
            <UserProfileDialog>
              <Button 
                variant="ghost" 
                size="sm"
                className={rightButtonStyle}
                title="Clique para ver informações da conta"
              >
                <User className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="hidden md:inline whitespace-nowrap overflow-hidden text-ellipsis max-w-[180px]">
                  {currentMember?.profile?.display_name || user.email || '\u00A0'}
                </span>
              </Button>
            </UserProfileDialog>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className={rightButtonStyle}
              title="Sair da conta"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="hidden md:inline">Sair</span>
            </Button>
          </div>
        )}
      </div>
    </header>
  );
};
